import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { Sheet, SheetContent, SheetTrigger } from "@loot-os/ui/components/sheet";
import { useIsMobile } from "@loot-os/ui/hooks/use-mobile";
import { formatUnreadBadge } from "@loot-os/ui/lib/notifications";
import { BellIcon } from "lucide-react";
import { useState } from "react";

import { notificationsEnabled, useUnreadNotifications } from "../../lib/notifications.js";
import { useShopper } from "../../lib/shopper.js";
import { useTenant } from "../../lib/tenant.js";
import { NotificationPanel } from "./NotificationPanel.js";

/** The header's bell, for a signed-in shopper on a store with notifications on. */
export function NotificationBell() {
  const { user } = useShopper();
  const { config } = useTenant();
  if (!user || !notificationsEnabled(config)) return null;
  return <Bell />;
}

/**
 * The bell and its unread badge, opening the panel in a popover, or a full-screen sheet on a
 * phone. Mounted once, it also keeps the count live and toasts what arrives.
 */
function Bell() {
  const unreadCount = useUnreadNotifications();
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const badge = formatUnreadBadge(unreadCount);
  const label = unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications";
  const trigger = (
    <Button variant="secondary" size="icon" className="relative" aria-label={label} />
  );
  const face = (
    <>
      <BellIcon />
      {badge && (
        <Badge
          aria-hidden
          className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 text-[0.625rem] tabular-nums ring-2 ring-background"
        >
          {badge}
        </Badge>
      )}
    </>
  );
  const close = () => setOpen(false);

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger render={trigger}>{face}</SheetTrigger>
        <SheetContent
          side="right"
          showCloseButton={false}
          aria-label="Notifications"
          className="w-full gap-0 data-[side=right]:w-full data-[side=right]:sm:max-w-none"
        >
          <NotificationPanel unreadCount={unreadCount} onClose={close} showClose />
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={trigger}>{face}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        aria-label="Notifications"
        className="max-h-[min(36rem,var(--available-height))] w-[380px] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0"
      >
        <NotificationPanel unreadCount={unreadCount} onClose={close} />
      </PopoverContent>
    </Popover>
  );
}
